/* eslint-disable array-bracket-newline, react-hooks/exhaustive-deps */
import { ITicker } from '@kattana-io/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Text from '../../../../atoms/Text';
import { switchCurrentTicker } from '../../../../Authlogic';
import {
  formatMobileTableData,
  IMobileTableScema,
} from '../../../../helpers/formatters';
import useTicker from '../../../../hooks/useTicker';
import DailyPriceChangeMobile from '../../../Watchlist/mobile/DailyPriceChange';
import PairIcons from '../../../../molecules/PairIcons';
import TableCardMobile from '../../../../molecules/Table/mobile/TableCard';
import { IStore } from '../../../../reducers';
import Loader from '../../../common/Loader';
import { toFixed2 } from '../../../DexTrades/formatters';
import { IMobileFilterProps } from '../../../SearchTable/SearchTable';

interface ITrendingTableMobileProps {
  inputSearch: string;
  onMount: (data: IMobileFilterProps) => void;
  onBack: () => void;
}

const useTrending = (trending_items: any[]) => {
  const [trending_items_ids, set_trending_items_ids] = useState<any[]>(
    trending_items.filter((i) => i.ticker_id) // remove nulls
  );

  const [trending_items_no_ids, set_trending_items_no_ids] = useState<any[]>(
    trending_items.filter((i) => !i.ticker_id) // Only nulls
  );

  // update items if they are loaded after component is mounted
  useEffect(() => {
    set_trending_items_ids(trending_items.filter((i) => i.ticker_id));

    set_trending_items_no_ids(trending_items.filter((i) => !i.ticker_id));
  }, [trending_items.length]);

  return {
    trending_items_ids,
    trending_items_no_ids,
  };
};

const TrendingTableMobile = ({
  inputSearch,
  onMount,
  onBack,
}: ITrendingTableMobileProps) => {
  const { items, isError, loading } = useSelector(
    (state: IStore) => state.trendingReducer
  );

  const { trending_items_ids, trending_items_no_ids } = useTrending(items);

  const [sortedData, setSortedData] = useState<any>([]);

  const { searchedWithId, searchedWithoutId, searchedSortedData } =
    useMemo(() => {
      const searchedWithoutId = trending_items_no_ids?.filter((item) =>
        item.label.toLocaleLowerCase().includes(inputSearch.toLocaleLowerCase())
      );
      const searchedWithId = trending_items_ids
        ?.map((item) => item.ticker)
        .filter((item) =>
          item?.name
            .toLocaleLowerCase()
            .includes(inputSearch.toLocaleLowerCase())
        );
      const searchedSortedData = sortedData?.tickers?.filter((item: ITicker) =>
        item?.name.toLocaleLowerCase().includes(inputSearch.toLocaleLowerCase())
      );
      return {
        searchedWithId,
        searchedWithoutId,
        searchedSortedData,
      };
    }, [trending_items_ids, trending_items_no_ids, inputSearch, sortedData]);

  useEffect(() => {
    onMount({
      setValue: setSortedData,
      initialData: {
        tickers: trending_items_ids?.map((item) => item.ticker) || [],
        selected: sortedData.selected,
      },
    });
  }, [onMount, setSortedData, trending_items_ids]);

  const handleCardClick = useCallback((data: any) => {
    switchCurrentTicker(data?.ticker);
    onBack();
  }, []);

  const schema1: IMobileTableScema = {
    row_1: {
      item_1: {
        accessor: 'pair',
        parser: (_, row) => row.label,
      },
      item_2: {
        accessor: 'price',
        parser: (_, row) => `$${toFixed2(row.price)}`,
      },
    },
  };

  const schema2: IMobileTableScema = {
    row_1: {
      item_1: {
        accessor: 'pair',
        parser: (_, row) => {
          const item = useTicker(row);
          return (
            <div className='ticker__cell'>
              <PairIcons
                chain={item.chain}
                pair={item.name}
                base_address={item.base_address}
                quote_address={item.quote_address}
                base_slug={item.base_slug}
                quote_slug={item.quote_slug}
              />
              <div className='ticker__cell__name_exchange'>
                <Text size={14} className='text--weight-500 text--grey-400'>
                  {item.name}
                </Text>
                <Text size={12} className='text--weight-500 text--grey-600'>
                  {item.exchange}
                </Text>
              </div>
            </div>
          );
        },
      },
      item_2: {
        accessor: 'price',
        parser: (_, row) => {
          return <DailyPriceChangeMobile ticker={row} />;
        },
      },
    },
  };

  const parsedData = (data: any, hasId: boolean) => {
    return hasId
      ? formatMobileTableData({
        data,
        schema: schema2,
        getColorForItem: () => 'grey',
        handleCardClick,
        keyName: 'id',
      })
      : formatMobileTableData({
        data,
        schema: schema1,
        getColorForItem: () => 'grey',
      });
  };

  const data =
    searchedSortedData?.length > 0 ? searchedSortedData : searchedWithId;

  return (
    <>
      {loading ? (
        <div className='centrify'>
          <Loader />
        </div>
      ) : (!searchedWithoutId?.length && !searchedWithId?.length) || isError ? (
        <div className='centrify'>No data found</div>
      ) : (
        <>
          {parsedData(searchedWithoutId, false)?.map((row, index) => (
            <TableCardMobile key={index} data={{ ...row, opened: true }} />
          ))}
          {parsedData(data, true)?.map((row) => (
            <TableCardMobile key={row.key} data={{ ...row, opened: true }} />
          ))}
        </>
      )}
    </>
  );
};

export default TrendingTableMobile;
